import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {GetPipelineStepShape} from 'pipelines/PipelineTypes';
import {
  BASE_FONT_PROXIMA,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {assertNever} from 'util/Types';

import {DataLayoutSection as DataLayoutSectionType} from './DataLayoutTypes';
import GridSection from './GridSection';
import TabSection from './TabSection';

interface DataLayoutSectionProps {
  layoutPath: Array<string | number>;
  section: DataLayoutSectionType;
}

export type HeadingLevel = GetPipelineStepShape<
  PipelineStepType.LAYOUT,
  0
>['config']['sections']['elementSpec']['choices'][0]['fields']['titleConfig']['fields']['headingLevel']['choices'][number];

/** @package */
export function SectionTitle({
  title,
  headingLevel,
}: {
  title: string;
  headingLevel: HeadingLevel;
}): JSX.Element {
  return (
    <div
      className={css(
        styles.sharedSectionHeading,
        headingLevel === 'TITLE' ? styles.sectionTitle : styles.sectionSubtitle
      )}
    >
      {title}
    </div>
  );
}

/** @package */
export default function DataLayoutSection({layoutPath, section}: DataLayoutSectionProps): JSX.Element {
  let sectionContents;
  if (section.type === 'GRID') {
    sectionContents = <GridSection grid={section.grid} layoutPath={layoutPath} />;
  } else if (section.type === 'TAB') {
    sectionContents = <TabSection tabSection={section} layoutPath={layoutPath} />;
  } else {
    assertAndContinue(false, 'Found unexpected section type in layout', {
      sectionType: assertNever(section).type,
    });
    sectionContents = null;
  }
  const headingLevel = (section.type === 'TAB' ? 'TITLE' : section.titleConfig?.headingLevel) ?? 'TITLE';
  return (
    <>
      {section.title && <SectionTitle title={section.title} headingLevel={headingLevel} />}
      {sectionContents}
    </>
  );
}

const styles = StyleSheet.create({
  sharedSectionHeading: {
    ...BASE_FONT_PROXIMA,
    fontSize: 22,
    margin: '10px 0',
  },
  sectionTitle: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  sectionSubtitle: {
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
});
